const WISHLIST_STORAGE_KEY = 'theme:wishlist-products';

function extractWishlistSectionId(element) {
  const section = element?.closest('.shopify-section');
  return section?.id.replace('shopify-section-', '');
}

class WishlistStorage {
  static getItems() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  static setItems(items) {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  static has(productId) {
    const id = parseInt(productId, 10);
    return WishlistStorage.getItems().includes(id);
  }

  static add(productId) {
    const id = parseInt(productId, 10);
    const items = WishlistStorage.getItems().filter((item) => item !== id);
    items.unshift(id);
    WishlistStorage.setItems(items);
  }

  static remove(productId) {
    const id = parseInt(productId, 10);
    WishlistStorage.setItems(WishlistStorage.getItems().filter((item) => item !== id));
  }

  static toggle(productId) {
    if (WishlistStorage.has(productId)) {
      WishlistStorage.remove(productId);
    } else {
      WishlistStorage.add(productId);
    }
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this._button = this.querySelector('button');

    if (!this._button || !this.dataset.productId) {
      return;
    }

    this._onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      WishlistStorage.toggle(this.dataset.productId);
    };
    this._onUpdate = () => this._updateState();

    this._button.addEventListener('click', this._onClick);
    document.addEventListener('wishlist:updated', this._onUpdate);
    this._updateState();
  }

  disconnectedCallback() {
    this._button?.removeEventListener('click', this._onClick);
    document.removeEventListener('wishlist:updated', this._onUpdate);
  }

  _updateState() {
    const isActive = WishlistStorage.has(this.dataset.productId);

    this.classList.toggle('is-active', isActive);
    this._button.setAttribute('aria-pressed', String(isActive));

    this.querySelector('[data-wishlist-icon-outline]')?.toggleAttribute('hidden', isActive);
    this.querySelector('[data-wishlist-icon-filled]')?.toggleAttribute('hidden', !isActive);

    const labelAdd = this.dataset.labelAdd;
    const labelRemove = this.dataset.labelRemove;

    if (labelAdd && labelRemove) {
      this._button.setAttribute('aria-label', isActive ? labelRemove : labelAdd);
    }
  }
}

class WishlistHeaderIcon extends HTMLElement {
  connectedCallback() {
    this._onUpdate = () => this._updateState();
    document.addEventListener('wishlist:updated', this._onUpdate);
    this._updateState();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this._onUpdate);
  }

  _updateState() {
    const hasItems = WishlistStorage.getItems().length > 0;

    this.classList.toggle('is-active', hasItems);
    this.querySelector('[data-wishlist-icon-outline]')?.toggleAttribute('hidden', hasItems);
    this.querySelector('[data-wishlist-icon-filled]')?.toggleAttribute('hidden', !hasItems);
  }
}

class WishlistProducts extends HTMLElement {
  connectedCallback() {
    this._onUpdate = () => this._loadProducts();
    document.addEventListener('wishlist:updated', this._onUpdate);
    this._loadProducts();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this._onUpdate);
  }

  async _loadProducts() {
    const section = this.closest('.shopify-section');

    if (!section) {
      return;
    }

    const items = WishlistStorage.getItems();
    const emptyState = section.querySelector('[data-wishlist-empty]');
    const contentWrapper = section.querySelector('[data-wishlist-content]');

    if (items.length === 0) {
      emptyState?.classList.remove('hidden');
      contentWrapper?.classList.add('hidden');
      this.replaceChildren();
      return;
    }

    emptyState?.classList.add('hidden');
    contentWrapper?.classList.remove('hidden');

    const query = items.map((id) => `id:${id}`).join(' OR ');
    const sectionId = extractWishlistSectionId(this);
    const url = `${Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${sectionId}`;

    try {
      const response = await fetch(url, { priority: 'low' });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetched = doc.querySelector('wishlist-products');

      if (fetched?.childElementCount > 0) {
        this.replaceChildren(...document.importNode(fetched, true).childNodes);
      } else {
        emptyState?.classList.remove('hidden');
        contentWrapper?.classList.add('hidden');
        this.replaceChildren();
      }
    } catch (error) {
      console.warn('Wishlist: failed to load products', error);
    }
  }
}

if (!window.customElements.get('wishlist-button')) {
  window.customElements.define('wishlist-button', WishlistButton);
}

if (!window.customElements.get('wishlist-header-icon')) {
  window.customElements.define('wishlist-header-icon', WishlistHeaderIcon);
}

if (!window.customElements.get('wishlist-products')) {
  window.customElements.define('wishlist-products', WishlistProducts);
}
